import Phaser from 'phaser';
import { audio } from '../audio/instances';
import { getLocale, t } from '../i18n';
import { PALETTE } from '../theme';

export class GameModeSelectScene extends Phaser.Scene {
  private dom: HTMLDivElement | null = null;
  private onLocaleChanged = () => this.rebuildDom();

  constructor() {
    super({ key: 'GameModeSelect' });
  }

  create(): void {
    this.cameras.main.setBackgroundColor(PALETTE.bg);
    this.dom = this.buildDom();
    document.body.appendChild(this.dom);

    window.addEventListener('locale-changed', this.onLocaleChanged);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.teardown());
  }

  private rebuildDom(): void {
    this.dom?.remove();
    this.dom = this.buildDom();
    document.body.appendChild(this.dom);
  }

  private buildDom(): HTMLDivElement {
    const dom = document.createElement('div');
    Object.assign(dom.style, {
      position: 'fixed',
      inset: '0',
      display: 'flex',
      flexDirection: 'column',
      color: '#d8dee9',
      fontFamily: "system-ui, -apple-system, 'Segoe UI', sans-serif",
      background: '#0b0d10 url("wallpaper.png") center/cover no-repeat',
      zIndex: '10',
    } as Partial<CSSStyleDeclaration>);

    const style = document.createElement('style');
    style.textContent = `
      .mode-tile {
        cursor: pointer;
        transition: filter 0.15s, transform 0.15s;
        border-radius: 8px;
        line-height: 0;
        flex: 0 0 auto;
        /* Width-bound on viewport WIDTH (≤ ~40%) and viewport HEIGHT (so
           the 638×502 aspect ratio never pushes the tile taller than the
           available screen). The smaller of the two wins via min(). */
        width: min(38vw, 70vh, 638px);
      }
      .mode-tile:hover { filter: brightness(1.15); transform: scale(1.03); }
      .mode-tile:active { filter: brightness(0.9); transform: scale(0.97); }
      .mode-tile img {
        display: block;
        width: 100%;
        height: auto;
        aspect-ratio: 638 / 502;
      }
    `;
    dom.appendChild(style);

    const body = document.createElement('div');
    Object.assign(body.style, {
      flex: '1',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '24px',
      overflow: 'hidden',  // scrollbars don't fit the design — shrink instead
      minHeight: '0',
    } as Partial<CSSStyleDeclaration>);

    const tilesRow = document.createElement('div');
    Object.assign(tilesRow.style, {
      display: 'flex',
      gap: '32px',
      justifyContent: 'center',
      alignItems: 'center',
      // Stay on one row at all viewport widths — tiles shrink proportionally.
      flexWrap: 'nowrap',
      width: '100%',
      maxWidth: '1308px', // 638 * 2 + 32 gap = natural max
      minWidth: '0',
    } as Partial<CSSStyleDeclaration>);

    const lang = getLocale() === 'pl' ? 'pl' : 'en';
    tilesRow.appendChild(this.buildTile('campaign-tile', lang, 'Campaign', true));
    tilesRow.appendChild(this.buildTile('single-scenario-tile', lang, 'MapSelect'));
    body.appendChild(tilesRow);
    dom.appendChild(body);

    const footer = document.createElement('div');
    Object.assign(footer.style, {
      display: 'flex',
      justifyContent: 'flex-end',
      gap: '10px',
      padding: '14px 20px',
      borderTop: '1px solid #2a3140',
    } as Partial<CSSStyleDeclaration>);

    const backBtn = document.createElement('button');
    backBtn.className = 'img-btn';
    backBtn.textContent = t('campaign.back');
    backBtn.style.cssText =
      "display:inline-block;width:150px;height:46px;border:none;cursor:pointer;" +
      "background:url('/graphics/ui/cancel-button-background.png') no-repeat;" +
      "background-size:100% 100%;background-color:transparent;color:#E8DCC2;" +
      "font-family:'Arial Black','Trebuchet MS',Impact,sans-serif;" +
      "font-weight:bold;font-size:13px;letter-spacing:2px;text-transform:uppercase;" +
      "line-height:46px;text-align:center;transition:filter 0.15s,transform 0.08s";
    const backHoverStyle = document.createElement('style');
    backHoverStyle.textContent =
      '.img-btn:hover{filter:brightness(1.2)}.img-btn:active{filter:brightness(0.9);transform:scale(0.97)}';
    dom.appendChild(backHoverStyle);
    backBtn.addEventListener('click', () => {
      audio.sfx.play('ui-click');
      this.scene.start('MainMenu');
    });
    footer.appendChild(backBtn);
    dom.appendChild(footer);

    return dom;
  }

  private buildTile(graphicBase: string, lang: 'pl' | 'en', sceneKey: string, disabled = false): HTMLDivElement {
    const tile = document.createElement('div');
    tile.className = 'mode-tile';
    tile.style.position = 'relative';

    const img = document.createElement('img');
    img.src = `/graphics/ui/${graphicBase}-${lang}.png`;
    // Intrinsic size kept for the browser's aspect-ratio hint; final size
    // is driven by CSS (width: 100% inside `.mode-tile`).
    img.draggable = false;
    tile.appendChild(img);

    if (disabled) {
      // Locked mode — greyed out, not clickable, with a "coming soon" badge.
      tile.style.opacity = '0.45';
      tile.style.filter = 'grayscale(0.9)';
      tile.style.cursor = 'not-allowed';
      tile.style.pointerEvents = 'none';
      const badge = document.createElement('div');
      badge.textContent = t('campaign.locked');
      badge.style.cssText =
        "position:absolute;top:50%;left:50%;transform:translate(-50%,-50%) rotate(-8deg);" +
        "padding:8px 22px;background:rgba(8,10,14,0.82);border:2px solid #E8DCC2;border-radius:6px;" +
        "color:#E8DCC2;font-family:'Arial Black','Trebuchet MS',Impact,sans-serif;" +
        "font-weight:bold;font-size:22px;letter-spacing:3px;text-transform:uppercase;white-space:nowrap";
      tile.appendChild(badge);
      return tile;
    }

    tile.addEventListener('click', () => {
      audio.sfx.play('ui-click');
      this.scene.start(sceneKey);
    });

    return tile;
  }

  private teardown(): void {
    window.removeEventListener('locale-changed', this.onLocaleChanged);
    this.dom?.remove();
    this.dom = null;
  }
}
